import React, { useState } from 'react';
import SearchBar from './components/SearchBar';
import FilterBar from './components/FilterBar';
import ImageGrid from './components/ImageGrid';
import Login from './components/Login';
import SearchFeedback from './components/SearchFeedback';
import SimilarImagesPanel from './components/SimilarImagesPanel';
import {
  fetchImages,
  fetchGroupImages,
  fetchImagesByImage,
  logout,
} from './api';

const defaultFilters = {
  folder: '',
  dateFrom: '',
  dateTo: '',
  weightFrom: '',
  weightTo: '',
  weightUnit: 'KB',
  widthFrom: '',
  widthTo: '',
  heightFrom: '',
  heightTo: '',
  limit: '',
};

const App = () => {
  const [isAuth, setIsAuth] = useState(!!localStorage.getItem('token'));
  const [query, setQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState(defaultFilters);

  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [groupImages, setGroupImages] = useState([]);
  const [groupLoading, setGroupLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const normalizeImage = (item) => ({
    ...item,
    groupId: item.groupId ?? item.group_id ?? item.id,
    groupSize: item.groupSize ?? item.group_size ?? 1,
    isGroupCover: item.isGroupCover ?? item.is_group_cover ?? true,
    fullPath: item.fullPath ?? item.full_path ?? '',
  });

  const resetUiState = () => {
    setQuery('');
    setShowFilters(false);
    setFilters(defaultFilters);
    setImages([]);
    setLoading(false);
    setSelectedGroupId(null);
    setGroupImages([]);
    setGroupLoading(false);
    setSelectedImage(null);
  };

  const handleLogin = () => {
    resetUiState();
    setIsAuth(true);
  };

  const handleLogout = () => {
    logout();
    resetUiState();
    setIsAuth(false);
  };

  const handleSearch = async () => {
    setLoading(true);

    try {
      const result = await fetchImages({ query, ...filters });
      setImages((result || []).map(normalizeImage));
    } catch (e) {
      console.error('Ошибка поиска:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleImageSearch = async (file) => {
    setLoading(true);

    try {
      const result = await fetchImagesByImage(file, filters);
      setImages((result || []).map(normalizeImage));
    } catch (e) {
      console.error('Ошибка поиска по изображению:', e);
      alert(e.message || 'Ошибка поиска по изображению');
    } finally {
      setLoading(false);
    }
  };

  const handleImageClick = async (image) => {
    const normalizedImage = normalizeImage(image);

    setSelectedImage(normalizedImage);
    setSelectedGroupId(normalizedImage.groupId);
    setGroupImages([normalizedImage]);
    setGroupLoading(true);

    try {
      if ((normalizedImage.groupSize ?? 1) > 1 && filters.folder) {
        const result = await fetchGroupImages(
          normalizedImage.groupId,
          filters.folder
        );

        const normalized = (result || []).map(normalizeImage);
        setGroupImages(normalized.length ? normalized : [normalizedImage]);
      } else {
        setGroupImages([normalizedImage]);
      }
    } catch (e) {
      console.error('Ошибка загрузки изображений:', e);
      setGroupImages([normalizedImage]);
    } finally {
      setGroupLoading(false);
    }
  };

  const handleClosePanel = () => {
    setSelectedGroupId(null);
    setGroupImages([]);
    setGroupLoading(false);
    setSelectedImage(null);
  };

  if (!isAuth) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <div style={{ padding: '20px', fontFamily: 'sans-serif' }}>
      <button
        onClick={handleLogout}
        style={{
          marginBottom: '16px',
          padding: '8px 14px',
          borderRadius: '10px',
          border: 'none',
          background: '#004DCF',
          color: '#fff',
          cursor: 'pointer',
        }}
      >
        Выйти
      </button>

      <SearchBar
        query={query}
        setQuery={setQuery}
        onSearch={handleSearch}
        onImageSearch={handleImageSearch}
        showFilters={showFilters}
        setShowFilters={setShowFilters}
      />

      <FilterBar
        filters={filters}
        setFilters={setFilters}
        showFilters={showFilters}
      />

      {loading ? (
        <p>Загрузка...</p>
      ) : (
        <>
          {images.length > 0 && (
            <SearchFeedback filters={filters} query={query} />
          )}

          <ImageGrid images={images} onImageClick={handleImageClick} />

          <SimilarImagesPanel
            isOpen={!!selectedImage}
            images={groupImages}
            loading={groupLoading}
            selectedImage={selectedImage}
            onSelectImage={setSelectedImage}
            onClose={handleClosePanel}
          />
        </>
      )}
    </div>
  );
};

export default App;